/**
 * 글 보기 화면의 순수 계산 (U3 · 2026-09-15) — 화면 파일에서 떼어 검사가 직접 부른다.
 */

/**
 * 목록이 글을 열 때 넘기는 «받아 둔 목록» (계획 §2.4 · 시안 문구 「이전·다음은 지금 목록에 받아 둔 글에서 이동합니다」).
 *
 * 화면마다 주소가 따로라(결정 Q5) 목록이 메모리에 없을 수 있다. 목록에서 들어오면 라우터 state 로 받고,
 * 알림 · 주소 직접 입력으로 들어오면 **없다** — 그때 이전·다음은 숨긴다(서버 이웃 조회는 요청하지 않음).
 */
export interface BoardListNavState {
  boardId: string;
  ids: string[];
  titles: Record<string, string>;
  /** 돌아갈 목록의 쿼리(`?sort=views` 등) — 「‹ 목록」이 필터를 잃지 않게. */
  search: string;
}

export function makeNavState(
  boardId: string,
  posts: ReadonlyArray<{ id: string; title: string }>,
  search: string,
): { boardList: BoardListNavState } {
  return {
    boardList: {
      boardId,
      ids: posts.map((p) => p.id),
      titles: Object.fromEntries(posts.map((p) => [p.id, p.title])),
      search,
    },
  };
}

/** 라우터 state 를 믿기 전에 모양을 본다 — 다른 화면이 넘긴 state 일 수 있다. */
export function readNavState(state: unknown): BoardListNavState | null {
  const v = (state as { boardList?: Partial<BoardListNavState> } | null | undefined)?.boardList;
  if (!v || typeof v.boardId !== 'string' || !Array.isArray(v.ids) || !v.ids.every((x) => typeof x === 'string')) return null;
  return {
    boardId: v.boardId,
    ids: v.ids,
    titles: v.titles && typeof v.titles === 'object' ? v.titles : {},
    search: typeof v.search === 'string' ? v.search : '',
  };
}

export interface Neighbours {
  /** 1부터 센다. */
  index: number;
  total: number;
  prev: { id: string; title: string } | null;
  next: { id: string; title: string } | null;
}

/** 받아 둔 목록 안에서의 앞뒤 — 그 목록에 없는 글이면 null(고정된 글만 따로 연 경우 등). */
export function neighbours(nav: BoardListNavState, postId: string): Neighbours | null {
  const i = nav.ids.indexOf(postId);
  if (i < 0) return null;
  const at = (j: number) => {
    const id = nav.ids[j];
    return j >= 0 && id != null ? { id, title: nav.titles[id] ?? '' } : null;
  };
  return { index: i + 1, total: nav.ids.length, prev: at(i - 1), next: at(i + 1) };
}

const pad = (n: number) => String(n).padStart(2, '0');

/** 정본 F18 「기록 · 표」 — `YYYY-MM-DD HH:mm`. 글 머리의 올린 시각. */
export function formatRecordDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * 댓글 수 — 뿌리 + 답글(깊이 무관). 서버 목록은 삭제된 댓글을 주지 않으므로
 * **삭제된 댓글은 수에 들어가지 않는다**(관제 지시 · 시안 문구 「댓글 수에 넣지 않습니다」).
 */
export function countComments(list: ReadonlyArray<{ replies?: ReadonlyArray<unknown> | null }>): number {
  let n = 0;
  const walk = (rows: ReadonlyArray<{ replies?: ReadonlyArray<unknown> | null }>) => {
    for (const r of rows) {
      n += 1;
      if (Array.isArray(r.replies)) walk(r.replies as ReadonlyArray<{ replies?: ReadonlyArray<unknown> | null }>);
    }
  };
  walk(list);
  return n;
}

/** 첨부 개수 · 합계 바이트. 서버가 `sizeBytes`(+ 하위호환 `size`)를 준다. */
export function attachmentSummary(atts: ReadonlyArray<{ sizeBytes?: number | null; size?: number | null }>): { count: number; bytes: number } {
  return {
    count: atts.length,
    bytes: atts.reduce((sum, a) => sum + (Number(a.sizeBytes ?? a.size) || 0), 0),
  };
}
